import { Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr, useDisclosure } from '@chakra-ui/react'
import { useEffect, useRef, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import { BackLink } from '../../../components/BackLink'
import { EmptyCell } from '../../../components/EmptyCell'
import { FilterBar, FilterSearch, FilterSelect } from '../../../components/FilterBar'
import { TableSkeletonRows } from '../../../components/Loading'
import { PageTitle } from '../../../components/PageTitle'
import Paginator, { DEFAULT_PAGE_SIZE } from '../../../components/paginator'
import { SectionCard } from '../../../components/SectionCard'
import { StatusBadge, statusOptions } from '../../../components/StatusBadge'
import { TableEmptyRow, TableErrorRow } from '../../../components/TableEmptyRow'
import { formatCurrency, formatDateTime, useAppToast } from '../../../helpers'
import { EventService } from '../../../services/Event/EventService'
import { EventTabsService } from '../../../services/Event/EventTabsService'
import { EventTabStatus, EventTabSummary } from '../../../services/Event/types'
import EventTabDrawer from './tabs/EventTabDrawer'
import { checkoutLabel, machineLabel, TAB_STATUS } from './tabs/tabLabels'

const COLUMNS = 7

export default function EventTabsPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const toast = useAppToast()
  const eventService = new EventService()
  const tabsService = new EventTabsService()

  const [eventId, setEventId] = useState<number | null>(null)
  const [tabs, setTabs] = useState<EventTabSummary[]>([])
  const [total, setTotal] = useState(0)
  const [page, setPage] = useState(1)
  const [pageSize, setPageSize] = useState(DEFAULT_PAGE_SIZE)
  const [loading, setLoading] = useState(true)
  const [loadError, setLoadError] = useState(false)

  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<EventTabStatus | ''>('')
  const [removalsFilter, setRemovalsFilter] = useState('')

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const { isOpen: isDrawerOpen, onOpen: onDrawerOpen, onClose: onDrawerClose } = useDisclosure()

  // Descarta respostas de `list()` que chegam fora de ordem quando os filtros mudam em sequência.
  const requestIdRef = useRef(0)

  useEffect(() => {
    // Outro evento: nada do anterior (comandas, filtros, gaveta) pode continuar na tela.
    setEventId(null)
    setTabs([])
    setTotal(0)
    setPage(1)
    setLoading(true)
    setLoadError(false)
    setSearch('')
    setStatusFilter('')
    setRemovalsFilter('')
    setSelectedId(null)
    onDrawerClose()
    requestIdRef.current++

    eventService
      .getBySlug(slug!)
      .then((data) => setEventId(data.id))
      .catch(() => {
        toast.error('Evento não encontrado')
        navigate('/events')
      })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [slug])

  // Sem timer aqui: o FilterSearch já espera a digitação parar, e um segundo atraso só deixava
  // a troca de página e de filtro sem resposta por 400 ms. Respostas fora de ordem caem no
  // requestIdRef.
  useEffect(() => {
    if (eventId === null) return
    fetchTabs()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId, search, statusFilter, removalsFilter, page, pageSize])

  function fetchTabs() {
    if (eventId === null) return
    const reqId = ++requestIdRef.current
    setLoading(true)
    setLoadError(false)
    tabsService
      .list(eventId, {
        search: search || undefined,
        status: statusFilter || undefined,
        with_removals: removalsFilter ? 1 : undefined,
        page,
        page_size: pageSize
      })
      .then((res) => {
        if (reqId !== requestIdRef.current) return
        setTabs(res.data)
        setTotal(res.total)
      })
      .catch((error) => {
        if (reqId !== requestIdRef.current) return
        setLoadError(true)
        toast.error('Erro ao carregar as comandas', error)
      })
      .finally(() => {
        if (reqId !== requestIdRef.current) return
        setLoading(false)
      })
  }

  function openTab(tab: EventTabSummary) {
    setSelectedId(tab.id)
    onDrawerOpen()
  }

  const totalPages = Math.ceil(total / pageSize)

  return (
    <>
      <BackLink to={`/events/${slug}/admin`} mb={5}>
        Voltar para o Evento
      </BackLink>

      <PageTitle
        title="Comandas"
        description="O que cada comanda consumiu e pagou, e quem lançou ou removeu cada item"
        mb={5}
      />

      <SectionCard>
        <FilterBar
          onClear={() => {
            setSearch('')
            setStatusFilter('')
            setRemovalsFilter('')
            setPage(1)
          }}
          isDirty={search !== '' || statusFilter !== '' || removalsFilter !== ''}
        >
          <FilterSearch
            value={search}
            onChange={(value) => {
              setSearch(value)
              setPage(1)
            }}
            placeholder="Buscar por nome ou cartão..."
            minW={{ md: '240px' }}
          />
          <FilterSelect
            label="Situação"
            maxW={{ md: '180px' }}
            value={statusFilter}
            onChange={(value) => {
              setStatusFilter(value as EventTabStatus | '')
              setPage(1)
            }}
            options={statusOptions(TAB_STATUS)}
            emptyLabel="Todas"
          />
          <FilterSelect
            label="Remoções"
            maxW={{ md: '200px' }}
            value={removalsFilter}
            onChange={(value) => {
              setRemovalsFilter(value)
              setPage(1)
            }}
            options={[{ value: '1', label: 'Só com remoções' }]}
            emptyLabel="Todas"
          />
        </FilterBar>

        <TableContainer>
          <Table>
            <Thead>
              <Tr>
                <Th>Cartão</Th>
                <Th>Participante</Th>
                <Th>Entrada</Th>
                <Th>Saída</Th>
                <Th isNumeric>Consumido</Th>
                <Th isNumeric>Pago</Th>
                <Th isNumeric>Removido</Th>
              </Tr>
            </Thead>
            <Tbody>
              {loading ? (
                <TableSkeletonRows cols={COLUMNS} rows={8} />
              ) : loadError ? (
                <TableErrorRow colSpan={COLUMNS} entity="comandas" feminine onRetry={fetchTabs} />
              ) : tabs.length === 0 ? (
                <TableEmptyRow colSpan={COLUMNS} entity="comanda" feminine />
              ) : (
                tabs.map((tab) => (
                  <Tr
                    key={tab.id}
                    role="button"
                    tabIndex={0}
                    _hover={{ bg: 'gray.50', cursor: 'pointer' }}
                    onClick={() => openTab(tab)}
                    onKeyDown={(event) => {
                      if (event.key === 'Enter' || event.key === ' ') {
                        event.preventDefault()
                        openTab(tab)
                      }
                    }}
                  >
                    <Td>
                      <Text fontFamily="mono">{tab.tab_code}</Text>
                    </Td>
                    <Td>
                      <EmptyCell value={tab.participant?.name} fontWeight="500" />
                    </Td>
                    <Td>
                      <EmptyCell value={formatDateTime(tab.checkin_at)} />
                      <Text variant="caption">{machineLabel(tab.checkin_machine)}</Text>
                    </Td>
                    <Td>
                      {tab.status === 'open' ? (
                        <StatusBadge status={tab.status} statuses={TAB_STATUS} />
                      ) : (
                        <>
                          <EmptyCell value={formatDateTime(tab.checkout_at)} />
                          <Text variant="caption">{checkoutLabel(tab)}</Text>
                        </>
                      )}
                    </Td>
                    <Td isNumeric>{formatCurrency(tab.consumed_total)}</Td>
                    <Td isNumeric>{formatCurrency(tab.paid_total)}</Td>
                    <Td isNumeric>
                      {tab.removals_count > 0 ? (
                        <>
                          <Text color="orange.600">{formatCurrency(tab.removed_total)}</Text>
                          <Text variant="caption">
                            {tab.removals_count} {tab.removals_count === 1 ? 'item' : 'itens'}
                          </Text>
                        </>
                      ) : (
                        <EmptyCell />
                      )}
                    </Td>
                  </Tr>
                ))
              )}
            </Tbody>
          </Table>
        </TableContainer>

        {totalPages > 1 && (
          <Paginator
            page={page}
            totalPages={totalPages}
            changePage={setPage}
            pageSize={pageSize}
            onPageSizeChange={(size) => {
              setPageSize(size)
              setPage(1)
            }}
          />
        )}

        {!loading && tabs.length > 0 && (
          <Text variant="caption" mt={3} textAlign="right">
            {total} comanda{total === 1 ? '' : 's'}
          </Text>
        )}
      </SectionCard>

      <EventTabDrawer eventId={eventId} checkinId={selectedId} isOpen={isDrawerOpen} onClose={onDrawerClose} />
    </>
  )
}
